import { createApi } from "@reduxjs/toolkit/query/react";
import {
  Attorney,
  LawFirm,
  LawFirmPostRequest,
  AttorneyPatchRequest,
  File,
  AttorneyFilePostRequest,
  AttorneyGetPayload,
} from "../../../api/src/types";
import type { AttorneyStatesGetResponse } from "../../../api/src/users/attorney/attorney-states/route";
import type { GetAttorneyClientsResponse } from "../../../api/src/users/attorney/clients/route";
import type { GetAttorneyClientResponse } from "../../../api/src/users/attorney/clients/route";
import { baseQuery } from "./utils";
import { usersApi } from "./users";
import { lawFirmsApi } from "./law-firms";
import type { AttorneyReviewsGetResponse } from "../../../api/src/users/attorney/attorney-reviews/route";
import {
  AttorneyUpdateEventResponse,
  AttorneyMarkEventNoShowResponse,
} from "../../../api/src/users/attorney/events/route";
import { AttorneyQuestionnaireSectionsGetResponse } from "../../../api/src/users/attorney/questionnaire-sections/route";
import { AttorneyQuestionGroupGetResponse } from "../../../api/src/users/attorney/questionnaire-sections/groups/route";
import {
  CreateAttorneyInternalNoteResponse,
  GetAttorneyInternalNoteResponse,
  PatchAttorneyInternalNoteResponse,
} from "../../../api/src/users/attorney/clients/internal-notes/route";
import type { AttorneysAdminGetResponse } from "../../../api/src/admin/attorneys/route";
import type { SingleAttorneyAdminGetResponse } from "../../../api/src/admin/attorneys/route";
import type {
  AttorneyServicesAdminPutRequest,
  AttorneyServicesAdminPutResponse,
} from "../../../api/src/admin/attorneys/attorney-services/route";
import type { AttorneyServicesGetResponse } from "../../../api/src/users/attorney/attorney-services/route";
import type { FeaturedAttorneyGetResponse } from "../../../api/src/users/attorney-services/route";
import type {
  DisciplinaryRecordsGetResponse,
  DisciplinaryRecordsPatchResponse,
  DisciplinaryRecordsPostResponse,
} from "../../../api/src/admin/attorneys/disciplinary-records/route";
import type {
  AdminEventsGetResponse,
  AdminMarkEventNoShowResponse,
  AdminMarkEventCompleteResponse,
  AdminRescheduleEventRequest,
  AdminRescheduleEventResponse,
  AdminSwitchEventAttorneyResponse,
  AdminReinstateEventResponse,
  AdminMarkEventInProgressResponse,
} from "../../../api/src/admin/events/route";

export const attorneysApi = createApi({
  reducerPath: "attorneysApi",
  baseQuery: baseQuery,
  tagTypes: [
    "Attorney",
    "LawFirm",
    "User",
    "AttorneyState",
    "AttorneyService",
    "AttorneyFiles",
    "AttorneyReview",
    "Clients",
    "QuestionnaireSections",
    "QuestionGroups",
    "InternalNotes",
    "AttorneyDisciplinaryRecord",
  ],
  endpoints: (builder) => ({
    getUserAttorney: builder.query<AttorneyGetPayload, string>({
      query: (id) => ({
        url: `users/${id}/attorneys`,
        method: "GET",
      }),
      providesTags: ["Attorney"],
    }),
    createLawFirm: builder.mutation<
      LawFirm,
      { userId: string; attorneyId: string } & LawFirmPostRequest
    >({
      query: ({ userId, attorneyId, ...bodyData }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/law-firms`,
        method: "POST",
        body: bodyData,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(lawFirmsApi.util.invalidateTags(["LawFirm"]));
      },
      invalidatesTags: ["LawFirm"],
    }),
    updateAttorney: builder.mutation<
      Attorney,
      { userId: string; attorneyId: string } & AttorneyPatchRequest
    >({
      query: ({ userId, attorneyId, ...bodyData }) => ({
        url: `users/${userId}/attorneys/${attorneyId}`,
        method: "PATCH",
        body: bodyData,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["User"]));
      },
      invalidatesTags: ["Attorney", "User", "AttorneyState"],
    }),
    getAttorneyLawFirm: builder.query<
      LawFirm,
      { userId: string; attorneyId: string }
    >({
      query: ({ userId, attorneyId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/law-firms`,
        method: "GET",
      }),
      providesTags: ["LawFirm"],
    }),
    getAttorneyStates: builder.query<
      AttorneyStatesGetResponse,
      { userId: string; attorneyId: string }
    >({
      query: ({ userId, attorneyId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/attorney-states`,
        method: "GET",
      }),
      providesTags: ["AttorneyState"],
    }),
    getAttorneyServices: builder.query<
      AttorneyServicesGetResponse,
      { userId: string; attorneyId: string }
    >({
      query: ({ userId, attorneyId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/attorney-services`,
        method: "GET",
      }),
      providesTags: ["AttorneyService"],
    }),
    getFeaturedAttorney: builder.query<
      FeaturedAttorneyGetResponse,
      { userId: string; docId: string }
    >({
      query: ({ userId, docId }) => ({
        url: `users/${userId}/documents/${docId}/attorneys/featured-attorney`,
        method: "GET",
      }),
      providesTags: ["Attorney"],
    }),
    createAttorneyFile: builder.mutation<
      File,
      { userId: string; attorneyId: string; file: AttorneyFilePostRequest }
    >({
      query: ({ userId, attorneyId, file }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/files`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["AttorneyFiles", "Attorney"],
    }),
    getAttorneyReviews: builder.query<
      AttorneyReviewsGetResponse,
      {
        userId: string;
        attorneyId: string;
        status: string;
        includeUser?: boolean;
      }
    >({
      query: ({ userId, attorneyId, status, includeUser }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/attorney-reviews?status=${status}&includeUser=${includeUser ?? false}`,
        method: "GET",
      }),
      providesTags: ["AttorneyReview"],
    }),
    getClients: builder.query<
      GetAttorneyClientsResponse,
      { userId: string; attorneyId: string }
    >({
      query: ({ userId, attorneyId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/clients`,
        method: "GET",
      }),
      providesTags: ["Clients"],
    }),
    getSingleClient: builder.query<
      GetAttorneyClientResponse,
      {
        userId: string;
        attorneyId: string;
        clientUserId: string;
        documentId: string;
      }
    >({
      query: ({ userId, attorneyId, clientUserId, documentId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/clients/${clientUserId}`,
        method: "GET",
        params: { documentId },
      }),
      providesTags: ["Clients"],
    }),
    updateEvent: builder.mutation<
      AttorneyUpdateEventResponse,
      { userId: string; attorneyId: string; eventId: string }
    >({
      query: ({ userId, attorneyId, eventId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/events/${eventId}/complete`,
        method: "POST",
      }),
      invalidatesTags: ["Clients"],
    }),
    updateEventNoShow: builder.mutation<
      AttorneyMarkEventNoShowResponse,
      { userId: string; attorneyId: string; eventId: string }
    >({
      query: ({ userId, attorneyId, eventId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/events/${eventId}/no-show`,
        method: "POST",
      }),
      invalidatesTags: ["Clients"],
    }),
    getAttorneyQuestionnaireSections: builder.query<
      AttorneyQuestionnaireSectionsGetResponse,
      { userId: string; attorneyId: string; documentId: string }
    >({
      query: ({ userId, attorneyId, documentId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/questionnaire-sections`,
        params: {
          documentId,
        },
        method: "GET",
      }),
      providesTags: ["QuestionnaireSections"],
    }),
    getAttorneyQuestionnaireSectionGroups: builder.query<
      AttorneyQuestionGroupGetResponse,
      {
        userId: string;
        attorneyId: string;
        sectionId: string;
        documentId: string;
        attorneyUserId: string;
      }
    >({
      query: ({
        userId,
        attorneyId,
        sectionId,
        documentId,
        attorneyUserId,
      }) => ({
        url: `users/${attorneyUserId}/attorneys/${attorneyId}/questionnaire-sections/${sectionId}/groups?documentId=${documentId}&userId=${userId}`,
        method: "GET",
      }),
      providesTags: (_, __, { userId }) => [
        "QuestionnaireSections",
        { type: "QuestionGroups", id: userId },
      ],
    }),
    createAttorneyInternalNote: builder.mutation<
      CreateAttorneyInternalNoteResponse,
      {
        userId: string;
        attorneyId: string;
        clientUserId: string;
        documentId: string;
      }
    >({
      query: ({ userId, attorneyId, clientUserId, documentId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/clients/${clientUserId}/internal-notes`,
        method: "POST",
        params: { documentId },
      }),
      invalidatesTags: (_, __, { clientUserId, documentId }) => [
        { type: "InternalNotes", id: `${clientUserId}_${documentId}` },
      ],
    }),
    getAttorneyInternalNote: builder.query<
      GetAttorneyInternalNoteResponse,
      {
        userId: string;
        attorneyId: string;
        clientUserId: string;
        documentId: string;
      }
    >({
      query: ({ userId, attorneyId, clientUserId, documentId }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/clients/${clientUserId}/internal-notes`,
        method: "GET",
        params: { documentId },
      }),
      providesTags: (_, __, { clientUserId, documentId }) => [
        { type: "InternalNotes", id: `${clientUserId}_${documentId}` },
      ],
    }),
    patchAttorneyInternalNote: builder.mutation<
      PatchAttorneyInternalNoteResponse,
      {
        userId: string;
        attorneyId: string;
        clientUserId: string;
        documentId: string;
        internalNoteId: string;
        message: string;
      }
    >({
      query: ({
        userId,
        attorneyId,
        clientUserId,
        documentId,
        internalNoteId,
        message,
      }) => ({
        url: `users/${userId}/attorneys/${attorneyId}/clients/${clientUserId}/internal-notes/${internalNoteId}`,
        method: "PATCH",
        params: { documentId },
        body: { message },
      }),
      invalidatesTags: (_, __, { clientUserId, documentId }) => [
        { type: "InternalNotes", id: `${clientUserId}_${documentId}` },
      ],
    }),
    getAttorneysAdmin: builder.query<AttorneysAdminGetResponse, void>({
      query: () => ({
        url: `admin/attorneys`,
        method: "GET",
      }),
      providesTags: ["Attorney"],
    }),
    getSingleAttorneyAdmin: builder.query<
      SingleAttorneyAdminGetResponse,
      { attorneyId: string }
    >({
      query: ({ attorneyId }) => ({
        url: `admin/attorneys/${attorneyId}`,
        method: "GET",
      }),
      providesTags: ["Attorney"],
    }),
    updateAttorneyServices: builder.mutation<
      AttorneyServicesAdminPutResponse,
      { attorneyId: string; body: AttorneyServicesAdminPutRequest }
    >({
      query: ({ attorneyId, body }) => ({
        url: `admin/attorneys/${attorneyId}/attorney-services`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AttorneyService"],
    }),
    getAdminAttorneyDisciplinaryRecords: builder.query<
      DisciplinaryRecordsGetResponse,
      { attorneyId: string }
    >({
      query: ({ attorneyId }) => ({
        url: `admin/attorneys/${attorneyId}/disciplinary-records`,
        method: "GET",
      }),
      providesTags: ["AttorneyDisciplinaryRecord"],
    }),
    createAdminAttorneyDisciplinaryRecord: builder.mutation<
      DisciplinaryRecordsPostResponse,
      { attorneyId: string }
    >({
      query: ({ attorneyId }) => ({
        url: `admin/attorneys/${attorneyId}/disciplinary-records`,
        method: "POST",
      }),
      invalidatesTags: ["AttorneyDisciplinaryRecord"],
    }),
    updateAdminAttorneyDisciplinaryRecord: builder.mutation<
      DisciplinaryRecordsPatchResponse,
      { attorneyId: string; disciplinaryRecordId: string }
    >({
      query: ({ attorneyId, disciplinaryRecordId }) => ({
        url: `admin/attorneys/${attorneyId}/disciplinary-records/${disciplinaryRecordId}`,
        method: "PATCH",
      }),
      invalidatesTags: ["AttorneyDisciplinaryRecord"],
    }),
    getAdminEvents: builder.query<AdminEventsGetResponse, { search?: string }>({
      query: ({ search }) => ({
        url: `admin/events`,
        method: "GET",
        params: { search },
      }),
      providesTags: ["Clients"],
    }),
    adminMarkEventNoShow: builder.mutation<
      AdminMarkEventNoShowResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `admin/events/${eventId}/no-show`,
        method: "POST",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
      },
      invalidatesTags: ["Clients"],
    }),
    adminMarkEventComplete: builder.mutation<
      AdminMarkEventCompleteResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `admin/events/${eventId}/complete`,
        method: "POST",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
      },
      invalidatesTags: ["Clients"],
    }),
    adminRescheduleEvent: builder.mutation<
      AdminRescheduleEventResponse,
      { eventId: string } & AdminRescheduleEventRequest
    >({
      query: ({ eventId, ...body }) => ({
        url: `admin/events/${eventId}/reschedule`,
        method: "POST",
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
      },
      invalidatesTags: ["Clients"],
    }),
    adminSwitchEventAttorney: builder.mutation<
      AdminSwitchEventAttorneyResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `admin/events/${eventId}/switch-attorney`,
        method: "POST",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
      },
      invalidatesTags: ["Clients"],
    }),
    adminReinstateEvent: builder.mutation<
      AdminReinstateEventResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `admin/events/${eventId}/reinstate`,
        method: "POST",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
      },
      invalidatesTags: ["Clients"],
    }),
    adminMarkEventInProgress: builder.mutation<
      AdminMarkEventInProgressResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `admin/events/${eventId}/uncomplete`,
        method: "POST",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
      },
      invalidatesTags: ["Clients"],
    }),
  }),
});

export const {
  useGetUserAttorneyQuery,
  useCreateLawFirmMutation,
  useUpdateAttorneyMutation,
  useGetAttorneyLawFirmQuery,
  useGetAttorneyStatesQuery,
  useGetAttorneyServicesQuery,
  useGetFeaturedAttorneyQuery,
  useCreateAttorneyFileMutation,
  useGetAttorneyReviewsQuery,
  useGetClientsQuery,
  useGetSingleClientQuery,
  useUpdateEventMutation,
  useUpdateEventNoShowMutation,
  useGetAttorneyQuestionnaireSectionsQuery,
  useGetAttorneyQuestionnaireSectionGroupsQuery,
  useCreateAttorneyInternalNoteMutation,
  useGetAttorneyInternalNoteQuery,
  usePatchAttorneyInternalNoteMutation,
  useGetAttorneysAdminQuery,
  useGetSingleAttorneyAdminQuery,
  useUpdateAttorneyServicesMutation,
  useGetAdminAttorneyDisciplinaryRecordsQuery,
  useCreateAdminAttorneyDisciplinaryRecordMutation,
  useUpdateAdminAttorneyDisciplinaryRecordMutation,
  useGetAdminEventsQuery,
  useAdminMarkEventNoShowMutation,
  useAdminMarkEventCompleteMutation,
  useAdminRescheduleEventMutation,
  useAdminSwitchEventAttorneyMutation,
  useAdminReinstateEventMutation,
  useAdminMarkEventInProgressMutation,
} = attorneysApi;
